import * as THREE from 'three';

/**
 * Sculpted car bodies, third vehicle pass. Pure geometry helpers (no three.js scene objects):
 *
 *  - `loftShell`: the lower body as a loft of cross-section profiles along the length. Each station z
 *    gets a half-section from the crowned top centre, round the shoulder radius, down the flank (with
 *    an optional swage crease), tucking under at the sill, with the wheel arches cut by raising the
 *    section's bottom along the arch circle. The arch liners, well walls and floor are part of the same
 *    grid, so there is nothing to see into. Pinch (rounded ends in plan) is built in.
 *  - `sweepCabin`: the greenhouse as a sweep of a crowned line across the cabin's side outline, so the
 *    screens curve in plan, the roof is cambered and the roof corners are filleted.
 *  - `sweepRect`: a rectangular bead along a polyline (door shut lines, waist chrome, seals, drip rails)
 *    that can follow a curved surface.
 *  - `panelLoft`: a slab (bonnet, boot lid, stripes) whose top follows the body's crowned surface.
 *
 * Geometries come back non-indexed with smooth normals and a `uv` attribute, ready for meshes.ts's
 * `tag()`. `userData.sculpted` marks pieces that already include the plan pinch, so the body kit does
 * not pinch them twice. Three detail levels: 2 = HIGH sculpt, 1 = MEDIUM, 0 = far impostor.
 */

export type Pt = [number, number];

export interface Arch {
  z: number;
  r: number;
  cy: number;
}

/** Cross-section character of a body (metres unless noted). */
export interface SectionStyle {
  /** Radius of the rounded top edge between deck/bonnet and flank. */
  corner: number;
  /** Fraction of the half width the flank pulls in at the sill (tuck-under). */
  tuck: number;
  /** Height where the tuck starts (above it the flank is full width). */
  tuckTop: number;
  /** Swage (crease) line height, or null. */
  swageY: number | null;
  /** Swage depth. */
  swage: number;
  /** Camber of the top surface at the centre line. */
  crown: number;
  /** Arch eyebrow: how far the flank flares out around the wheel arches. */
  flare: number;
}

export interface LoftSpec {
  /** Top line (z, y) rear→front in metres: the bonnet / belt deck / boot line. */
  top: Pt[];
  clearance: number;
  halfW: number;
  /** Plan shaping: width multiplier at z (1 = full width). */
  pinch: (z: number) => number;
  arches: Arch[];
  /** Inner wall x of the wheel wells. */
  wellX: number;
  section: SectionStyle;
  detail: 0 | 1 | 2;
}

export interface Shell {
  paint: THREE.BufferGeometry;
  dark: THREE.BufferGeometry;
  /** Half width at z including pinch. */
  hw(z: number): number;
  /** Top line height at z (no crown). */
  topY(z: number): number;
  /** Height of the crowned top surface at (z, x). */
  surfaceY(z: number, x: number): number;
  /** x of the flank at height y (outer paint surface), for parts that sit on the body side. */
  flankX(z: number, y: number): number;
  /** Bottom of the painted flank at z (arch circle or sill). */
  bottomY(z: number): number;
  /** Plan pinch at z (width multiplier). */
  pinch(z: number): number;
}

export function lineY(top: Pt[], z: number): number {
  if (z <= top[0]![0]) return top[0]![1];
  for (let i = 1; i < top.length; i++) {
    const a = top[i - 1]!;
    const b = top[i]!;
    if (z <= b[0]) return a[1] + ((b[1] - a[1]) * (z - a[0])) / Math.max(1e-6, b[0] - a[0]);
  }
  return top[top.length - 1]![1];
}

interface Prof {
  x: number;
  y: number;
}

/** Emit a non-indexed geometry from triangles with explicit normals. */
class Tris {
  pos: number[] = [];
  nrm: number[] = [];
  tri(a: number[], b: number[], c: number[], na: number[], nb: number[], nc: number[]): void {
    this.pos.push(a[0]!, a[1]!, a[2]!, b[0]!, b[1]!, b[2]!, c[0]!, c[1]!, c[2]!);
    this.nrm.push(na[0]!, na[1]!, na[2]!, nb[0]!, nb[1]!, nb[2]!, nc[0]!, nc[1]!, nc[2]!);
  }
  /** Quad a-b-c-d (counter-clockwise seen from outside) with per-corner normals. */
  quad(a: number[], b: number[], c: number[], d: number[], na: number[], nb: number[], nc: number[], nd: number[]): void {
    this.tri(a, b, c, na, nb, nc);
    this.tri(a, c, d, na, nc, nd);
  }
  build(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(this.nrm, 3));
    g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array((this.pos.length / 3) * 2), 2));
    g.userData.smooth = true;
    g.userData.sculpted = true;
    return g;
  }
}

function norm(v: number[]): number[] {
  const l = Math.hypot(v[0]!, v[1]!, v[2]!) || 1;
  return [v[0]! / l, v[1]! / l, v[2]! / l];
}

function cross(a: number[], b: number[]): number[] {
  return [a[1]! * b[2]! - a[2]! * b[1]!, a[2]! * b[0]! - a[0]! * b[2]!, a[0]! * b[1]! - a[1]! * b[0]!];
}

function sub(a: number[], b: number[]): number[] {
  return [a[0]! - b[0]!, a[1]! - b[1]!, a[2]! - b[2]!];
}

/**
 * Smooth vertex normals for a grid P[s][i] (s along the sweep, i along the profile), outward =
 * cross(d/ds, d/di). `hard` lists profile indices where the normal must not blend across (one-sided
 * differences on both sides of the break).
 */
function gridNormals(P: number[][][], hard: Set<number>): number[][][] {
  const S = P.length;
  const N = P[0]!.length;
  const out: number[][][] = [];
  for (let s = 0; s < S; s++) {
    const row: number[][] = [];
    for (let i = 0; i < N; i++) {
      const s0 = Math.max(0, s - 1);
      const s1 = Math.min(S - 1, s + 1);
      let i0 = Math.max(0, i - 1);
      let i1 = Math.min(N - 1, i + 1);
      if (hard.has(i)) i0 = i; // break: this vertex belongs to the segment below it
      if (hard.has(i + 1)) i1 = i;
      let ts = sub(P[s1]![i]!, P[s0]![i]!);
      let ti = sub(P[s]![i1]!, P[s]![i0]!);
      if (Math.hypot(ti[0]!, ti[1]!, ti[2]!) < 1e-6) ti = sub(P[s]![Math.min(N - 1, i + 2)]!, P[s]![Math.max(0, i - 2)]!);
      if (Math.hypot(ts[0]!, ts[1]!, ts[2]!) < 1e-6) ts = [0, 0, 1];
      let n = norm(cross(ts, ti));
      if (Math.hypot(n[0]!, n[1]!, n[2]!) < 0.5) n = [1, 0, 0];
      row.push(n);
    }
    out.push(row);
  }
  return out;
}

/** Emit the grid as quads; `mirror` flips x and winding. Rows r0..r1 of the profile only. */
function emitGrid(t: Tris, P: number[][][], Nrm: number[][][], i0: number, i1: number, mirror: boolean): void {
  const S = P.length;
  const m = (p: number[]): number[] => (mirror ? [-p[0]!, p[1]!, p[2]!] : p);
  for (let s = 0; s < S - 1; s++) {
    for (let i = i0; i < i1; i++) {
      const a = m(P[s]![i]!);
      const b = m(P[s + 1]![i]!);
      const c = m(P[s + 1]![i + 1]!);
      const d = m(P[s]![i + 1]!);
      const na = m(Nrm[s]![i]!);
      const nb = m(Nrm[s + 1]![i]!);
      const nc = m(Nrm[s + 1]![i + 1]!);
      const nd = m(Nrm[s]![i + 1]!);
      if (mirror) t.quad(a, d, c, b, na, nd, nc, nb);
      else t.quad(a, b, c, d, na, nb, nc, nd);
    }
  }
}

export function loftShell(spec: LoftSpec): Shell {
  const { top, clearance, halfW, pinch, arches, wellX, section: st, detail } = spec;
  const zR = top[0]![0];
  const zF = top[top.length - 1]![0];
  const hw = (z: number) => halfW * pinch(z);
  const topY = (z: number) => lineY(top, z);
  const archAt = (z: number): Arch | null => {
    for (const a of arches) if (Math.abs(z - a.z) <= a.r) return a;
    return null;
  };
  const bottomY = (z: number): number => {
    if (detail === 0) return clearance;
    const a = archAt(z);
    if (!a) return clearance;
    const dz = z - a.z;
    // The arch never cuts above the shoulder: low cars get a flat-topped well under the tyre.
    return Math.min(topY(z) - st.corner - 0.03, Math.max(clearance, a.cy + Math.sqrt(Math.max(0, a.r * a.r - dz * dz))));
  };
  const corner = detail === 0 ? st.corner * 0.5 : st.corner;
  const hwTop = (z: number) => Math.max(0.1, hw(z) - corner);
  const surfaceY = (z: number, x: number): number => {
    const ht = hwTop(z);
    const u = Math.min(1, Math.abs(x) / ht);
    return topY(z) + st.crown * (1 - u * u);
  };

  // --- half profile at a station: paint points (top centre → flank bottom), then dark (lip underside,
  // liner, well wall, floor). Fixed count per detail level so the grid stays regular.
  const topFr = detail === 2 ? [0, 0.45, 0.8, 1] : detail === 1 ? [0, 0.7, 1] : [0, 1];
  const arcAngles = detail === 2 ? [60, 30, 0] : detail === 1 ? [40, 0] : [0];
  const profile = (z: number): { pts: Prof[]; nPaint: number } => {
    const w = hw(z);
    const ht = hwTop(z);
    const yT = topY(z);
    const yb = bottomY(z);
    const inArch = yb > clearance + 1e-4;
    const pts: Prof[] = [];
    for (const f of topFr) pts.push({ x: ht * f, y: yT + st.crown * (1 - f * f) });
    for (const deg of arcAngles) {
      const a = (deg * Math.PI) / 180;
      pts.push({ x: ht + corner * Math.cos(a), y: yT - corner + corner * Math.sin(a) });
    }
    const yCorner = yT - corner;
    const flare = inArch ? st.flare : 0;
    const tuckAt = (y: number): number => {
      const t = Math.min(1, Math.max(0, (st.tuckTop - y) / Math.max(0.05, st.tuckTop - clearance - 0.05)));
      return 1 - st.tuck * t * t;
    };
    if (detail === 2) {
      // Swage crease: a ridge that catches the light along the flank (HIGH only).
      const sy = st.swageY;
      if (sy !== null && sy < yCorner - 0.04 && sy > yb + 0.06) {
        pts.push({ x: w * tuckAt(sy + 0.02), y: sy + 0.02 });
        pts.push({ x: w * tuckAt(sy) + st.swage, y: sy });
        pts.push({ x: w * tuckAt(sy - 0.02) - st.swage * 0.4, y: sy - 0.02 });
      } else {
        const y = Math.max(yb + 0.02, Math.min(yCorner - 0.02, (yCorner + yb) / 2));
        pts.push({ x: w * tuckAt(y), y }, { x: w * tuckAt(y), y }, { x: w * tuckAt(y), y });
      }
    }
    if (detail > 0) {
      const yt = Math.max(yb + 0.015, Math.min(yCorner - 0.01, st.tuckTop));
      pts.push({ x: w * tuckAt(yt) + flare * 0.5, y: yt });
    }
    const xb = w * tuckAt(yb) + flare;
    pts.push({ x: xb, y: yb });
    const nPaint = pts.length;
    const lip = inArch ? 0.012 : 0;
    pts.push({ x: xb - 0.016, y: yb - lip });
    pts.push({ x: Math.min(wellX, xb - 0.05), y: yb - lip });
    pts.push({ x: Math.min(wellX, xb - 0.05), y: clearance });
    pts.push({ x: 0, y: clearance });
    return { pts, nPaint };
  };

  // --- stations along z
  const zs = new Set<number>();
  for (const p of top) zs.add(p[0]);
  if (detail > 0) {
    const n = detail === 2 ? 10 : 5;
    for (const a of arches) {
      zs.add(a.z - a.r - 0.002);
      zs.add(a.z + a.r + 0.002);
      for (let k = 0; k <= n; k++) {
        const th = (k / n) * Math.PI;
        zs.add(a.z + Math.cos(th) * a.r);
      }
    }
  }
  const gapMax = detail === 2 ? 0.32 : detail === 1 ? 0.75 : 1.2;
  let stations = [...zs].filter((z) => z >= zR && z <= zF).sort((a, b) => a - b);
  const filled: number[] = [];
  for (let i = 0; i < stations.length; i++) {
    const z = stations[i]!;
    if (i > 0) {
      const prev = stations[i - 1]!;
      const gap = z - prev;
      const n = Math.floor(gap / gapMax);
      for (let k = 1; k <= n; k++) filled.push(prev + (gap * k) / (n + 1));
    }
    filled.push(z);
  }
  stations = filled;
  // Nose/tail roll: a couple of stations rounding the top edge into the end faces.
  const rollR = 0.05;
  const roll = (zEnd: number, dir: 1 | -1) => {
    if (detail === 0) return;
    for (const deg of [50, 20]) {
      const a = (deg * Math.PI) / 180;
      stations.push(zEnd - dir * rollR * (1 - Math.cos(a)));
    }
  };
  roll(zF, 1);
  roll(zR, -1);
  stations = [...new Set(stations)].sort((a, b) => a - b);

  // Build the grid.
  const P: number[][][] = [];
  let nPaint = 0;
  for (const z of stations) {
    const { pts, nPaint: np } = profile(z);
    nPaint = np;
    let yDrop = 0;
    // Round the top edge down toward the nose/tail face.
    const dF = zF - z;
    const dR = z - zR;
    const d = Math.min(dF, dR);
    if (detail > 0 && d < rollR) yDrop = rollR - Math.sqrt(Math.max(0, rollR * rollR - (rollR - d) * (rollR - d)));
    const nTop = topFr.length + arcAngles.length;
    P.push(pts.map((p, i) => [p.x, i < nTop ? p.y - yDrop : p.y, z]));
  }
  const hard = new Set<number>([nPaint]);
  const Nrm = gridNormals(P, hard);
  const paint = new Tris();
  const dark = new Tris();
  for (const mirror of [false, true]) {
    emitGrid(paint, P, Nrm, 0, nPaint - 1, mirror);
    emitGrid(dark, P, Nrm, nPaint - 1, P[0]!.length - 1, mirror);
  }
  // End caps: fan over the full section at the first and last station.
  const cap = (s: number, nz: 1 | -1) => {
    const row = P[s]!;
    const ring: number[][] = [];
    for (let i = 0; i < nPaint; i++) ring.push(row[i]!);
    ring.push([0, clearance, row[0]![2]!]);
    const full: number[][] = [];
    for (let i = ring.length - 1; i >= 1; i--) full.push([-ring[i]![0]!, ring[i]![1]!, ring[i]![2]!]);
    for (let i = 0; i < ring.length; i++) full.push(ring[i]!);
    const yT = row[0]![1]!;
    const c = [0, (yT + clearance) / 2, row[0]![2]!];
    const n = [0, 0, nz];
    for (let i = 0; i < full.length - 1; i++) {
      const a = full[i]!;
      const b = full[i + 1]!;
      if (nz > 0) paint.tri(c, b, a, n, n, n);
      else paint.tri(c, a, b, n, n, n);
    }
  };
  // The nose/tail faces: winding so that they face out of the body.
  cap(P.length - 1, 1);
  cap(0, -1);
  const paintGeo = paint.build();
  const darkGeo = dark.build();
  // Flat-shade the caps: they were emitted with their own normal already.
  const flankX = (z: number, y: number): number => {
    const { pts, nPaint: np } = profile(z);
    const first = topFr.length + arcAngles.length - 1; // corner end (widest)
    if (y >= pts[first]!.y) return pts[first]!.x;
    for (let i = first; i < np - 1; i++) {
      const a = pts[i]!;
      const b = pts[i + 1]!;
      if (y <= a.y && y >= b.y) {
        const t = a.y === b.y ? 0 : (a.y - y) / (a.y - b.y);
        return a.x + (b.x - a.x) * t;
      }
    }
    return pts[np - 1]!.x;
  };
  return { paint: paintGeo, dark: darkGeo, hw, topY, surfaceY, flankX, bottomY, pinch };
}

// ------------------------------------------------------------------------------------ greenhouse

export interface CabinSweep {
  glass: THREE.BufferGeometry;
  /** Painted roof skin over the roof segment (with a small skirt at the edges), null if no roof. */
  roof: THREE.BufferGeometry | null;
  /** Outline samples (z, y) along the path, rear base → front base, after filleting. */
  path: Pt[];
  /** Roof segment range in path parameter space (indices into `path`). */
  roofRange: [number, number];
}

/**
 * Sweep a crowned line across the cabin's side outline [rear base, rear roof, front roof, front base].
 * Screens bulge in plan (curved glass), the roof cambers up, roof corners are filleted.
 */
export function sweepCabin(outline: Pt[], halfW: number, crownScreen: number, crownRoof: number, detail: 0 | 1 | 2, roofSkin: boolean): CabinSweep {
  const [rb, rr, fr, fb] = outline as [Pt, Pt, Pt, Pt];
  // Path with filleted roof corners.
  const fillet = detail === 0 ? 0 : 0.1;
  const path: Pt[] = [];
  const seg: number[] = []; // 0 rear screen, 1 roof, 2 front screen (per path point)
  const nScreen = detail === 2 ? 4 : detail === 1 ? 2 : 1;
  const nRoof = detail === 2 ? 4 : detail === 1 ? 2 : 1;
  const lerp = (a: Pt, b: Pt, t: number): Pt => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
  const len = (a: Pt, b: Pt) => Math.hypot(b[0] - a[0], b[1] - a[1]);
  const fR = Math.min(fillet / len(rb, rr), fillet / len(rr, fr), 0.45);
  const fF = Math.min(fillet / len(fr, fb), fillet / len(rr, fr), 0.45);
  const push = (p: Pt, s: number) => {
    path.push(p);
    seg.push(s);
  };
  for (let k = 0; k <= nScreen; k++) push(lerp(rb, rr, ((1 - fR) * k) / nScreen), 0);
  // fillet rr: quadratic from rr-fR·(rb→rr) through rr to rr+fR·(rr→fr)
  const bez = (a: Pt, c: Pt, b: Pt, t: number): Pt => [
    (1 - t) * (1 - t) * a[0] + 2 * (1 - t) * t * c[0] + t * t * b[0],
    (1 - t) * (1 - t) * a[1] + 2 * (1 - t) * t * c[1] + t * t * b[1],
  ];
  if (fillet > 0) {
    const a = lerp(rb, rr, 1 - fR);
    const b = lerp(rr, fr, fR);
    for (const t of [0.35, 0.7]) push(bez(a, rr, b, t), 1);
  }
  const roofStart = path.length;
  for (let k = 0; k <= nRoof; k++) push(lerp(rr, fr, fR + ((1 - fR - fF) * k) / nRoof), 1);
  const roofEnd = path.length - 1;
  if (fillet > 0) {
    const a = lerp(rr, fr, 1 - fF);
    const b = lerp(fr, fb, fF);
    for (const t of [0.3, 0.65]) push(bez(a, fr, b, t), 1);
  }
  for (let k = 0; k <= nScreen; k++) push(lerp(fr, fb, fF + ((1 - fF) * k) / nScreen), 2);

  // Outward normal along the path (z,y): n = (-dy, dz).
  const normals: Pt[] = path.map((_p, i) => {
    const a = path[Math.max(0, i - 1)]!;
    const b = path[Math.min(path.length - 1, i + 1)]!;
    const dz = b[0] - a[0];
    const dy = b[1] - a[1];
    const l = Math.hypot(dz, dy) || 1;
    return [-dy / l, dz / l];
  });
  const crownOf = (i: number): number => {
    const s = seg[i]!;
    if (s === 1) {
      // Fillets and roof: blend from the screen crown to the roof crown.
      if (i < roofStart) return crownScreen + ((crownRoof - crownScreen) * (i - (roofStart - 3))) / 3;
      if (i > roofEnd) return crownRoof + ((crownScreen - crownRoof) * (i - roofEnd)) / 3;
      return crownRoof;
    }
    return crownScreen;
  };
  const xs = detail === 2 ? [-1, -0.75, -0.45, 0, 0.45, 0.75, 1] : detail === 1 ? [-1, -0.5, 0, 0.5, 1] : [-1, 0, 1];
  const grid = (offset: number, skirt: number, i0: number, i1: number): THREE.BufferGeometry => {
    const P: number[][][] = [];
    for (let i = i0; i <= i1; i++) {
      const p = path[i]!;
      const n = normals[i]!;
      const c = crownOf(i);
      const row: number[][] = [];
      if (skirt > 0) row.push([-halfW * 1.012 + 0, p[1] + n[1] * offset - skirt, p[0] + n[0] * offset]);
      for (const u of xs) {
        const bulge = c * (1 - u * u) + offset;
        row.push([u * halfW, p[1] + n[1] * bulge, p[0] + n[0] * bulge]);
      }
      if (skirt > 0) row.push([halfW * 1.012, p[1] + n[1] * offset - skirt, p[0] + n[0] * offset]);
      P.push(row);
    }
    const hard = new Set<number>();
    if (skirt > 0) {
      hard.add(1);
      hard.add(P[0]!.length - 1);
    }
    const Nrm = gridNormals(P, hard);
    const t = new Tris();
    emitGrid(t, P, Nrm, 0, P[0]!.length - 1, false);
    return t.build();
  };
  const glass = grid(0, 0, 0, path.length - 1);
  // Side panes: fan over the outline at x = ±halfW.
  {
    const t = new Tris();
    const c = [0, (rb[1] + rr[1] + fr[1] + fb[1]) / 4, (rb[0] + rr[0] + fr[0] + fb[0]) / 4];
    for (const sx of [1, -1]) {
      const n = [sx, 0, 0];
      const cc = [sx * halfW, c[1]!, c[2]!];
      const ring = path.map((p) => [sx * halfW, p[1], p[0]]);
      ring.push([sx * halfW, fb[1], fb[0]], [sx * halfW, rb[1], rb[0]]);
      for (let i = 0; i < ring.length - 1; i++) {
        if (sx > 0) t.tri(cc, ring[i]!, ring[i + 1]!, n, n, n);
        else t.tri(cc, ring[i + 1]!, ring[i]!, n, n, n);
      }
    }
    const side = t.build();
    const merged = mergeNonIndexed([glass, side]);
    glass.dispose();
    side.dispose();
    const roof = roofSkin ? grid(0.012, 0.035, Math.max(0, roofStart - 2), Math.min(path.length - 1, roofEnd + 2)) : null;
    merged.userData.sculpted = false;
    if (roof) roof.userData.sculpted = false;
    return { glass: merged, roof, path, roofRange: [roofStart, roofEnd] };
  }
}

function mergeNonIndexed(gs: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const pos: number[] = [];
  const nrm: number[] = [];
  for (const g of gs) {
    pos.push(...(g.getAttribute('position').array as Float32Array));
    nrm.push(...(g.getAttribute('normal').array as Float32Array));
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  out.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  out.setAttribute('uv', new THREE.BufferAttribute(new Float32Array((pos.length / 3) * 2), 2));
  out.userData.smooth = true;
  out.userData.sculpted = true;
  return out;
}

// ------------------------------------------------------------------------------------ beads and panels

/**
 * A rectangular bead swept along `pts`: corners p ± a ± b (a, b are the half-extent vectors of the
 * cross-section). Flat-shaded per face; closed with end caps.
 */
export function sweepRect(pts: number[][], a: number[], b: number[]): THREE.BufferGeometry {
  const t = new Tris();
  const corner = (p: number[], sa: number, sb: number): number[] => [p[0]! + a[0]! * sa + b[0]! * sb, p[1]! + a[1]! * sa + b[1]! * sb, p[2]! + a[2]! * sa + b[2]! * sb];
  const faces: [number, number, number, number][] = [
    [1, 1, 1, -1], // +a side: corners (+a,+b) → (+a,-b)
    [1, -1, -1, -1], // -b side
    [-1, -1, -1, 1], // -a side
    [-1, 1, 1, 1], // +b side
  ];
  for (let i = 0; i < pts.length - 1; i++) {
    const p = pts[i]!;
    const q = pts[i + 1]!;
    for (const [sa0, sb0, sa1, sb1] of faces) {
      const p0 = corner(p, sa0, sb0);
      const p1 = corner(p, sa1, sb1);
      const q0 = corner(q, sa0, sb0);
      const q1 = corner(q, sa1, sb1);
      const out = sa0 === sa1 ? [a[0]! * sa0, a[1]! * sa0, a[2]! * sa0] : [b[0]! * sb0, b[1]! * sb0, b[2]! * sb0];
      let n = norm(cross(sub(q0, p0), sub(p1, p0)));
      if (n[0]! * out[0]! + n[1]! * out[1]! + n[2]! * out[2]! >= 0) t.quad(p0, q0, q1, p1, n, n, n, n);
      else {
        n = [-n[0]!, -n[1]!, -n[2]!];
        t.quad(p0, p1, q1, q0, n, n, n, n);
      }
    }
  }
  const capAt = (p: number[], dir: number[]) => {
    const n = norm(dir);
    const c0 = corner(p, 1, 1);
    const c1 = corner(p, 1, -1);
    const c2 = corner(p, -1, -1);
    const c3 = corner(p, -1, 1);
    const f = cross(sub(c1, c0), sub(c2, c0));
    if (f[0]! * n[0]! + f[1]! * n[1]! + f[2]! * n[2]! > 0) t.quad(c0, c1, c2, c3, n, n, n, n);
    else t.quad(c0, c3, c2, c1, n, n, n, n);
  };
  capAt(pts[0]!, sub(pts[0]!, pts[1]!));
  capAt(pts[pts.length - 1]!, sub(pts[pts.length - 1]!, pts[pts.length - 2]!));
  const g = t.build();
  g.userData.smooth = false;
  return g;
}

/**
 * A slab over the body's crowned surface between z0..z1 and x0..x1, from yLo to yHi above it. Used for
 * bonnets, boot lids, shut-line slabs and stripes.
 */
export function panelLoft(shell: Shell, z0: number, z1: number, x0: number, x1: number, yLo: number, yHi: number, nz: number, nx: number): THREE.BufferGeometry {
  const t = new Tris();
  const top: number[][][] = [];
  const bot: number[][][] = [];
  for (let i = 0; i <= nz; i++) {
    const z = z0 + ((z1 - z0) * i) / nz;
    const rt: number[][] = [];
    const rb: number[][] = [];
    const k = shell.pinch(z);
    for (let j = 0; j <= nx; j++) {
      const x = (x0 + ((x1 - x0) * j) / nx) * k;
      const y = shell.surfaceY(z, x);
      rt.push([x, y + yHi, z]);
      rb.push([x, y + yLo, z]);
    }
    top.push(rt);
    bot.push(rb);
  }
  const nt = gridNormals(top, new Set());
  // Grid orientation: s = z (forward), i = x (increasing). cross(+z, +x) = +y: outward is up. Good.
  emitGrid(t, top, nt, 0, nx, false);
  // Underside (flipped winding, down normal).
  const down = [0, -1, 0];
  for (let i = 0; i < nz; i++) for (let j = 0; j < nx; j++) t.quad(bot[i]![j]!, bot[i]![j + 1]!, bot[i + 1]![j + 1]!, bot[i + 1]![j]!, down, down, down, down);
  // Skirts.
  const skirt = (ra: number[][], rb: number[][], n: number[], flip: boolean) => {
    for (let k = 0; k < ra.length - 1; k++) {
      if (flip) t.quad(ra[k]!, ra[k + 1]!, rb[k + 1]!, rb[k]!, n, n, n, n);
      else t.quad(ra[k]!, rb[k]!, rb[k + 1]!, ra[k + 1]!, n, n, n, n);
    }
  };
  skirt(top[nz]!, bot[nz]!, [0, 0, 1], false); // front edge
  skirt(top[0]!, bot[0]!, [0, 0, -1], true); // rear edge
  const colL = top.map((r) => r[nx]!);
  const colLb = bot.map((r) => r[nx]!);
  const colR = top.map((r) => r[0]!);
  const colRb = bot.map((r) => r[0]!);
  skirt(colL, colLb, [1, 0, 0], true);
  skirt(colR, colRb, [-1, 0, 0], false);
  return t.build();
}
